import { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  TextField,
  Card,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridEventListener,
} from '@mui/x-data-grid'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { LinkedService } from 'src/v3/core/types'
import ConfirmAction from 'src/v3/components/confirm-action'
import {
  fetchConnectClients,
  fetchLinkedServices,
  fetchConnectClientLinkedServices,
  addConnectClientLinkedService,
  removeConnectClientLinkedService,
  deleteConnectClient,
  selectConnectClients,
  selectConnectClientsStatus,
  selectConnectClientById,
  selectSelectedConnectClient,
  selectConnectClientMappings,
  selectConnectClientMappingsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectedConnectClientChanged,
} from '../admin.slice'
import CreateConnectClientForm from './forms/create'
import EditConnectClientForm from './forms/edit'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let connectClients = useAppSelector(selectConnectClients)
  let connectClientsStatus = useAppSelector(selectConnectClientsStatus)
  let selectedClientId = useAppSelector(selectSelectedConnectClient)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let selectedClient = useAppSelector(selectConnectClientById(selectedClientId))
  let mappings = useAppSelector(selectConnectClientMappings(selectedClientId))
  let mappingsStatus = useAppSelector(
    selectConnectClientMappingsStatus(selectedClientId),
  )

  let [formOpen, setFormOpen] = useState<string>('')
  let [editingClient, setEditingClient] = useState<string>('')
  let [dialogOpen, setDialogOpen] = useState<string>('')
  let [deleteAction, setDeleteAction] = useState<(() => Promise<void>) | null>(
    null,
  )
  let [deleteTitle, setDeleteTitle] = useState<string>('')
  let [deleteText, setDeleteText] = useState<string>('')
  let [updating, setUpdating] = useState(false)
  let [searchValue, setSearchValue] = useState<LinkedService | null>(null)
  let [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  // Fetch initial data
  useEffect(() => {
    if (connectClientsStatus === 'uninitialized') {
      dispatch(fetchConnectClients())
    }
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [connectClientsStatus, linkedServicesStatus, dispatch])

  // Fetch mappings when a client is selected
  useEffect(() => {
    if (selectedClientId && mappingsStatus === 'uninitialized') {
      dispatch(fetchConnectClientLinkedServices(selectedClientId))
    }
  }, [selectedClientId, mappingsStatus, dispatch])

  let handleRowClick: GridEventListener<'rowClick'> = (params) => {
    dispatch(selectedConnectClientChanged(params.id as string))
  }

  let handleEdit = (id: string) => {
    setEditingClient(id)
    setFormOpen('edit')
  }

  let handleDelete = (id: string) => {
    let client = connectClients.find((c) => c.id === id)
    setDeleteTitle('Delete Connect Client')
    setDeleteText(
      `Are you sure you want to delete "${client?.clientName}"? This action cannot be undone.`,
    )
    setDeleteAction(() => async () => {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(deleteConnectClient(id)).unwrap()
      setSnackbar({
        open: true,
        message: 'Client deleted successfully',
        severity: 'success',
      })
      if (selectedClientId === id) {
        dispatch(selectedConnectClientChanged(''))
      }
    })
    setDialogOpen('confirm-delete')
  }

  let handleAddMapping = async () => {
    if (!searchValue || !selectedClientId) return

    setUpdating(true)
    try {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(
        addConnectClientLinkedService({
          id: selectedClientId,
          linkedServiceID: searchValue.id,
        }),
      ).unwrap()
      setSnackbar({
        open: true,
        message: 'Linked service added successfully',
        severity: 'success',
      })
      setSearchValue(null)
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to add linked service: ${error}`,
        severity: 'error',
      })
    } finally {
      setUpdating(false)
    }
  }

  let handleDeleteMapping = (linkedServiceID: string, name: string) => {
    setDeleteTitle('Remove Linked Service')
    setDeleteText(
      `Are you sure you want to remove access to "${name}" for this client?`,
    )
    setDeleteAction(() => async () => {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(
        removeConnectClientLinkedService({
          id: selectedClientId,
          linkedServiceId: linkedServiceID,
        }),
      ).unwrap()
      setSnackbar({
        open: true,
        message: 'Linked service removed successfully',
        severity: 'success',
      })
    })
    setDialogOpen('confirm-delete')
  }

  let columns: GridColDef[] = [
    {
      field: 'clientName',
      headerName: 'Client Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'clientID',
      headerName: 'Client ID',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'createdTime',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {new Date(params.value).toLocaleDateString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(params.row.id)
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete(params.row.id)
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  let mappingColumns: GridColDef[] = [
    {
      field: 'linkedServiceName',
      headerName: 'Linked Service Name',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'linkedServiceID',
      headerName: 'Linked Service ID',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'createdTime',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {new Date(params.value).toLocaleDateString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() =>
            handleDeleteMapping(
              params.row.linkedServiceID,
              params.row.linkedServiceName,
            )
          }
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ]

  let rows: GridRowsProp = connectClients.map((client) => ({
    id: client.id,
    clientName: client.clientName,
    clientID: client.clientID,
    createdTime: client.createdTime,
  }))

  let mappingRows: GridRowsProp = mappings.map((mapping) => ({
    id: mapping.id,
    linkedServiceID: mapping.linkedServiceID,
    linkedServiceName: mapping.linkedServiceName,
    createdTime: mapping.createdTime,
  }))

  let availableLinkedServices = linkedServices.filter(
    (ls) => !mappings.some((m) => m.linkedServiceID === ls.id),
  )

  return (
    <>
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            FOAMfrat Connect Clients
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 3 }}>
            Manage Connect API client applications and their linked service
            access
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => setFormOpen('create')}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Create Client
          </Button>
        </Box>

        <Paper sx={{ mb: 4, p: 3, bgcolor: 'background.paper' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={connectClientsStatus === 'pending'}
            onRowClick={handleRowClick}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              border: 'none',
              bgcolor: 'background.paperDark',
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'action.selected',
              },
            }}
          />
        </Paper>

        {selectedClient && (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 600, mb: 2 }}>
                {selectedClient.clientName}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Client ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedClient.clientID}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedClient.clientID)
                        setSnackbar({
                          open: true,
                          message: 'Client ID copied to clipboard',
                          severity: 'success',
                        })
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      selectedClient.createdTime || '',
                    ).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Card sx={{ p: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: 600, mb: 2 }}>
                Linked Service Access
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  Add Linked Service
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    options={availableLinkedServices}
                    getOptionLabel={(option) => option.name}
                    value={searchValue}
                    onChange={(_, newValue) => setSearchValue(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Linked Services"
                        placeholder="Type to search..."
                      />
                    )}
                    loading={linkedServicesStatus === 'pending'}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddMapping}
                    disabled={!searchValue || updating}
                    sx={{ minWidth: 100, height: 56 }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              <DataGrid
                rows={mappingRows}
                columns={mappingColumns}
                loading={mappingsStatus === 'pending'}
                autoHeight
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                sx={{ border: 'none', bgcolor: 'background.paperDark' }}
              />
            </Card>
          </Box>
        )}
      </Container>

      <CreateConnectClientForm
        isOpen={formOpen === 'create'}
        isSaving={(bool) => setUpdating(bool)}
        onClose={() => setFormOpen('')}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: 'Client created successfully',
            severity: 'success',
          })
        }}
      />

      <EditConnectClientForm
        isOpen={formOpen === 'edit'}
        clientID={editingClient}
        isSaving={(bool) => setUpdating(bool)}
        onClose={() => {
          setFormOpen('')
          setEditingClient('')
        }}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: 'Client updated successfully',
            severity: 'success',
          })
        }}
      />

      <ConfirmAction
        open={dialogOpen}
        setOpen={setDialogOpen}
        openText="confirm-delete"
        title={deleteTitle}
        text={deleteText}
        executeAction={deleteAction || (() => Promise.resolve())}
        onFailure={(err) => {
          setSnackbar({
            open: true,
            message: `Failed to delete: ${err}`,
            severity: 'error',
          })
        }}
      />

      <Backdrop
        open={updating}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
